import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Typography,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshIcon from '@mui/icons-material/Refresh';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropperDialog = ({ open, onClose, image, onCroppedImage }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
    aspect: 1
  });
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (open && image) {
      setImageLoadError(false);
      setImageLoaded(false);
      setScale(1);
      setRotate(0);
    }
  }, [open, image]);

  const onImageLoad = (e) => {
    try {
      setImageLoaded(true);
      const { width, height } = e.currentTarget;
      const initialCrop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          1,
          width,
          height
        ),
        width,
        height
      );
      setCrop(initialCrop);
    } catch (error) {
      console.error("Error initializing crop:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleCrop = async () => {
    if (!imgRef.current || !crop.width || !crop.height) {
      console.warn("No image or crop area defined");
      onCroppedImage(image);
      return;
    }

    try {
      const sourceImg = new Image();
      sourceImg.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        sourceImg.onload = resolve;
        sourceImg.onerror = reject;
        sourceImg.src = image;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = sourceImg;

      const displayImg = imgRef.current;

      const scaleX = displayImg.naturalWidth / displayImg.width;
      const scaleY = displayImg.naturalHeight / displayImg.height;

      let displayCrop;
      if (crop.unit === '%') {
        displayCrop = {
          x: (crop.x / 100) * displayImg.width,
          y: (crop.y / 100) * displayImg.height,
          width: (crop.width / 100) * displayImg.width,
          height: (crop.height / 100) * displayImg.height,
        };
      } else {
        displayCrop = {
          x: crop.x,
          y: crop.y,
          width: crop.width,
          height: crop.height,
        };
      }

      const zoomAdjustedCrop = {
        x: (displayCrop.x + (displayImg.width * (1 - 1 / scale)) / 2) * scaleX,
        y: (displayCrop.y + (displayImg.height * (1 - 1 / scale)) / 2) * scaleY,
        width: displayCrop.width * scaleX / scale,
        height: displayCrop.height * scaleY / scale,
      };

      const pixelCrop = zoomAdjustedCrop;

      let clampedCrop;
      if (pixelCrop.x >= img.naturalWidth || pixelCrop.y >= img.naturalHeight ||
        pixelCrop.x + pixelCrop.width <= 0 || pixelCrop.y + pixelCrop.height <= 0) {
        const centerX = img.naturalWidth / 2;
        const centerY = img.naturalHeight / 2;
        const cropSize = Math.min(img.naturalWidth, img.naturalHeight) * 0.5;
        clampedCrop = {
          x: centerX - cropSize / 2,
          y: centerY - cropSize / 2,
          width: cropSize,
          height: cropSize
        };
      } else {
        // Clamp to valid bounds
        clampedCrop = {
          x: Math.max(0, Math.min(pixelCrop.x, img.naturalWidth - 10)),
          y: Math.max(0, Math.min(pixelCrop.y, img.naturalHeight - 10)),
          width: Math.max(10, Math.min(pixelCrop.width, img.naturalWidth - Math.max(0, pixelCrop.x))),
          height: Math.max(10, Math.min(pixelCrop.height, img.naturalHeight - Math.max(0, pixelCrop.y)))
        };
        // Ensure crop doesn't extend beyond image bounds
        if (clampedCrop.x + clampedCrop.width > img.naturalWidth) {
          clampedCrop.width = img.naturalWidth - clampedCrop.x;
        }
        if (clampedCrop.y + clampedCrop.height > img.naturalHeight) {
          clampedCrop.height = img.naturalHeight - clampedCrop.y;
        }
      }
      // Validate crop dimensions
      if (clampedCrop.width <= 0 || clampedCrop.height <= 0) {
        console.error("❌ Invalid crop dimensions after clamping");
        throw new Error("Invalid crop dimensions");
      }
      // Set canvas size to the crop size
      canvas.width = Math.floor(clampedCrop.width);
      canvas.height = Math.floor(clampedCrop.height);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotate * Math.PI) / 180);

      ctx.drawImage(
        img,
        Math.floor(clampedCrop.x),
        Math.floor(clampedCrop.y),
        Math.floor(clampedCrop.width),
        Math.floor(clampedCrop.height),
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );

      ctx.restore();

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let hasNonTransparent = false;

      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > 0) {
          hasNonTransparent = true;
          break;
        }
      }

      try {
        try {
          ctx.getImageData(0, 0, 1, 1);
        } catch (taintError) {
          console.warn("⚠️ Canvas might be tainted:", taintError.message);
        }

        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);

        if (!dataUrl || dataUrl === 'data:,') {
          console.error("❌ Data URL is empty or invalid");
          throw new Error("Empty data URL");
        }

        const testImg = new Image();
        testImg.onload = () => {
          const cropData = {
            originalImage: image,
            crop: {
              x: crop.x,
              y: crop.y,
              width: crop.width,
              height: crop.height,
              unit: crop.unit
            },
            scale: scale,
            imageNaturalWidth: img.naturalWidth,
            imageNaturalHeight: img.naturalHeight,
            imageDisplayWidth: img.width,
            imageDisplayHeight: img.height,
            isDataUrl: true
          };
          onCroppedImage(dataUrl, cropData);
        };

        testImg.onerror = (error) => {
          console.error("❌ Data URL failed to load:", error);
          const cropData = {
            originalImage: image,
            crop: { x: crop.x, y: crop.y, width: crop.width, height: crop.height, unit: crop.unit },
            scale: scale,
            imageNaturalWidth: img.naturalWidth,
            imageNaturalHeight: img.naturalHeight,
            imageDisplayWidth: img.width,
            imageDisplayHeight: img.height,
            isFallback: true
          };
          onCroppedImage(image, cropData);
        };

        testImg.src = dataUrl;

      } catch (dataUrlError) {
        console.error("❌ Data URL creation failed:", dataUrlError);
        const cropData = {
          originalImage: image,
          crop: { x: crop.x, y: crop.y, width: crop.width, height: crop.height, unit: crop.unit },
          scale: scale,
          imageNaturalWidth: img.naturalWidth,
          imageNaturalHeight: img.naturalHeight,
          imageDisplayWidth: img.width,
          imageDisplayHeight: img.height,
          isFallback: true
        };
        onCroppedImage(image, cropData);
      }

    } catch (error) {
      console.error("❌ Error cropping image:", error);

      const cropData = {
        originalImage: image,
        crop: {
          x: crop.x,
          y: crop.y,
          width: crop.width,
          height: crop.height,
          unit: crop.unit
        },
        scale: scale,
        isFallback: true
      };

      onCroppedImage(image, cropData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Adjust Image
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflowY: 'auto',
          maxHeight: '70vh',
        }}
      >
        {/* Image Cropper Area */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {image && !imageLoadError && (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              aspect={1}
              circularCrop={false}
            >
              <img
                ref={imgRef}
                src={image}
                alt="Crop preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '50vh',
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s ease-out',
                }}
                onLoad={onImageLoad}
                onError={() => setImageLoadError(true)}
              />
            </ReactCrop>
          )}

          {/* 🔍 Zoom Control - FIRST PRIORITY */}
          {imageLoaded && !imageLoadError && (
            <Box sx={{ width: '90%', mt: 2, mb: 1 }}>
              <Typography gutterBottom>Zoom</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => setScale(Math.max(0.5, scale - 0.1))}>
                  <ZoomOutIcon />
                </IconButton>
                <Slider
                  value={scale}
                  min={0.5}
                  max={3}
                  step={0.1}
                  onChange={(_, newValue) => setScale(newValue)}
                  sx={{
                    mx: 2,
                    '& .MuiSlider-thumb': { color: '#3F0E77' },
                    '& .MuiSlider-track': { color: '#3F0E77' },
                    '& .MuiSlider-rail': { color: '#3F0E77', opacity: 0.3 },
                  }}
                />
                <IconButton onClick={() => setScale(Math.min(3, scale + 0.1))}>
                  <ZoomInIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Rotate Control - SECOND PRIORITY */}
          {imageLoaded && !imageLoadError && (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '90%', mt: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography gutterBottom>Rotate</Typography>
                <Slider
                  value={rotate}
                  min={-45}
                  max={45}
                  step={1}
                  onChange={(_, newValue) => setRotate(newValue)}
                  sx={{
                    '& .MuiSlider-thumb': { color: '#3F0E77' },
                    '& .MuiSlider-track': { color: '#3F0E77' },
                    '& .MuiSlider-rail': { color: '#3F0E77', opacity: 0.3 },
                  }}
                />
                <Typography align="center" sx={{ mt: -1 }}>{rotate}°</Typography>
              </Box>
              <IconButton
                onClick={() => setRotate(0)}
                sx={{
                  ml: 1,
                  border: '1px solid #3F0E77',
                  color: '#3F0E77',
                  '&:hover': {
                    backgroundColor: '#3F0E77',
                    color: 'white',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          sx={{
            border: '1px solid #3F0E77',
            backgroundColor: 'white',
            color: '#3F0E77',
            '&:hover': {
              backgroundColor: '#3F0E77',
              color: 'white',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleCrop()}
          sx={{
            backgroundColor: '#3F0E77',
            color: 'white',
            '&:hover': {
              backgroundColor: '#2E0B5A',
            },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropperDialog;